import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, API_URL } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { DashboardSkeleton } from '../components/Skeleton';
import PulseDot from '../components/PulseDot';
import Icon from '../components/Icon';
import CodeBlock from '../components/CodeBlock';
import { EmptySignal } from '../components/illustrations';
import { timeAgo } from '../lib/time';

const TONE = { active: 'ok', silent: 'crit', unknown: 'idle' };

function ServerRow({ srv, i, origin }) {
  const { t, i18n } = useTranslation();
  const { selectServer, reloadServers, activeServerKey } = useAuth();
  const lang = i18n.resolvedLanguage;
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(srv.label || '');
  const [busy, setBusy] = useState(false);
  const [expand, setExpand] = useState(false);

  const name = srv.label || t('servers.default_name', { n: (srv.order ?? i) + 1 });
  const suspended = srv.status !== 'active';

  const save = async () => {
    setBusy(true);
    try {
      await api.renameServer(srv.key, label.trim());
      await reloadServers(activeServerKey);
      setEditing(false);
    } finally { setBusy(false); }
  };

  return (
    <div className={`card srv-row ${suspended ? 'is-suspended' : ''}`}>
      <div className="srv-row-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <PulseDot tone={suspended ? 'idle' : TONE[srv.agentStatus] || 'idle'} animated={srv.agentStatus === 'active' && !suspended} />
          {editing ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60}
                placeholder={t('servers.name_placeholder')} style={{ width: 200 }} autoFocus />
              <button className="btn btn-sm" onClick={save} disabled={busy}>{t('common.save', 'Kaydet')}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setEditing(false); setLabel(srv.label || ''); }}>{t('common.cancel', 'Vazgeç')}</button>
            </div>
          ) : (
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {name}
                <button className="icon-btn" onClick={() => setEditing(true)} title={t('servers.rename')}>
                  <Icon name="copy" size={12} />
                </button>
                {suspended && <span className="srv-tag">{t('servers.suspended')}</span>}
              </div>
              <div style={{ color: 'var(--text-3)', fontSize: 12.5 }}>
                {suspended
                  ? t('servers.suspended_hint')
                  : !srv.lastHeartbeatAt
                    ? t('servers.never_connected')
                    : t(`servers.status_${srv.agentStatus}`, { ago: timeAgo(srv.lastHeartbeatAt, lang) })}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {srv.health && <span className={`srv-health ${srv.health.label}`}>{srv.health.score}</span>}
          {!suspended && (
            <button className="btn btn-subtle btn-sm" onClick={() => { selectServer(srv.key); }}>
              {srv.key === activeServerKey ? t('servers.viewing') : t('servers.open')}
            </button>
          )}
          {!suspended && (
            <button className="btn btn-ghost btn-sm" onClick={() => setExpand((v) => !v)}>
              {t('servers.setup')}
              <Icon name="arrow" size={12} style={{ transform: expand ? 'rotate(-90deg)' : 'rotate(90deg)', opacity: 0.5 }} />
            </button>
          )}
        </div>
      </div>

      {expand && !suspended && (
        <div className="srv-row-body">
          {srv.needsSetup && <p className="srv-setup-warn">{t('servers.setup_needed')}</p>}
          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>{t('onboarding.oneclick_linux')}</div>
          <CodeBlock code={`curl -fsSL ${origin}/kur/${srv.key} | sudo bash`} />
          <div style={{ fontSize: 12.5, fontWeight: 600, margin: '12px 0 6px' }}>{t('onboarding.oneclick_windows')}</div>
          <CodeBlock code={`irm ${origin}/kur/${srv.key}.ps1 | iex`} />
          <div style={{ fontSize: 12.5, fontWeight: 600, margin: '12px 0 6px' }}>{t('onboarding.oneclick_freebsd')}</div>
          <CodeBlock code={`fetch -qo - ${origin}/kur/${srv.key}.freebsd | sh`} />
          <Link to="/onboarding" onClick={() => selectServer(srv.key)} className="btn btn-subtle btn-sm" style={{ marginTop: 12 }}>
            {t('servers.full_setup')}
          </Link>
        </div>
      )}
    </div>
  );
}

export default function Servers() {
  const { t } = useTranslation();
  const { servers, serverSlots, loading, hasActiveLicense } = useAuth();
  const navigate = useNavigate();
  const [addCount, setAddCount] = useState(1);

  const origin = (API_URL && /^https?:\/\//.test(API_URL) ? API_URL : window.location.origin).replace(/\/+$/, '');
  const activeServers = servers.filter((s) => s.status === 'active');
  const freeSlots = Math.max(0, serverSlots - activeServers.length);

  if (loading) {
    return <DashboardLayout title={t('servers.title')} heading={false}><DashboardSkeleton /></DashboardLayout>;
  }

  if (!hasActiveLicense) {
    return (
      <DashboardLayout title={t('servers.title')} heading={false}>
        <h1 className="page-title">{t('servers.title')}</h1>
        <div className="empty-state">
          <EmptySignal />
          <h3>{t('servers.none_title')}</h3>
          <p>{t('servers.none_desc')}</p>
          <Link to="/checkout" className="btn btn-sm" style={{ marginTop: 'var(--sp-5)' }}>{t('checkout.title')}</Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t('servers.title')} heading={false}>
      <h1 className="page-title">{t('servers.title')}</h1>
      <p className="page-sub">{t('servers.subtitle', { active: activeServers.length, slots: serverSlots })}</p>

      <div className="list-plain">
        {servers.map((s, i) => <ServerRow key={s.key} srv={s} i={i} origin={origin} />)}
      </div>

      <div className="card" style={{ marginTop: 'var(--sp-4)' }}>
        <h3 style={{ fontSize: '1rem', marginBottom: 6 }}>{t('servers.add_title')}</h3>
        <p style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 14 }}>{t('servers.add_desc')}</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div className="qty-stepper">
            <button onClick={() => setAddCount((c) => Math.max(1, c - 1))} disabled={addCount <= 1}>−</button>
            <span>{addCount}</span>
            <button onClick={() => setAddCount((c) => Math.min(20, c + 1))}>+</button>
          </div>
          <button className="btn" onClick={() => navigate(`/checkout?add=${addCount}`)}>
            <Icon name="plus" size={14} /> {t('servers.add_cta', { n: addCount })}
          </button>
        </div>
        {freeSlots > 0 && (
          <p style={{ color: 'var(--ok)', fontSize: 12.5, marginTop: 12 }}>{t('servers.free_slots', { n: freeSlots })}</p>
        )}
      </div>
    </DashboardLayout>
  );
}
